import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Check, AlertCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || "");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<'magic' | 'password'>("password");
  const [isSignup, setIsSignup] = useState(true);
  const [step, setStep] = useState<"email" | "sent" | "processing" | "verified">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasProcessedToken = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Simplified auth handling - no more box claiming here
  useEffect(() => {
    let isSubscribed = true;
    let sessionHandled = false;

    const handleAuthenticatedUser = async (userId: string, userEmail: string) => {
      if (sessionHandled || !isSubscribed) return;
      sessionHandled = true;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowRetry(false);
      setStep("verified");
      
      // Simply navigate to dashboard - box is already saved!
      toast({
        title: "Welcome to Endless Storage!",
        description: "You're now logged in.",
      });
      navigate('/dashboard');
    };

    const processAuthToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');

      // Handle errors from magic link
      if (errorCode) {
        setError(errorDescription || 'Magic link expired or invalid. Please try again.');
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
        return;
      }

      // If we have tokens, process them directly
      if (accessToken && refreshToken && !hasProcessedToken.current) {
        hasProcessedToken.current = true;
        setStep("processing");
        
        timeoutRef.current = setTimeout(() => setShowRetry(true), 10000);
        
        try {
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
          
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            setError('Failed to complete login. Please try again.');
            setStep("email");
            return;
          }

          if (data?.session?.user) {
            handleAuthenticatedUser(data.session.user.id, data.session.user.email!);
          }
        } catch (err) {
          setError('Failed to complete login. Please try again.');
          setStep("email");
        }
      } else {
        // No tokens in URL - check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && isSubscribed) {
          navigate('/dashboard');
        }
      }
    };

    // Set up auth listener as fallback
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user && !sessionHandled && isSubscribed) {
        setTimeout(() => {
          handleAuthenticatedUser(session.user.id, session.user.email!);
        }, 0);
      }
    });

    processAuthToken();

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [navigate, toast]);

  // handleClaimBox removed - boxes are now created immediately on "Save to Account"

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      const redirectUrl = `${window.location.origin}/signup`;
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;

      setStep("sent");
      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link.",
      });
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast({ title: "Welcome back!", description: "You're now logged in." });
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/signup`;
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectUrl },
      });
      if (signUpError) throw signUpError;

      // Send welcome email
      if (signUpData.user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.functions.invoke('send-signup-welcome', {
            body: { email, name: email.split('@')[0] }
          });
        }
      }

      // Immediately sign in to avoid email link flow
      const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError) throw loginError;

      toast({ title: "Account created!", description: "You're now logged in." });
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary">Endless Storage</span>
          </div>
          <p className="text-muted-foreground">Digital inventory made simple</p>
        </div>

        <Card className="shadow-elegant">
          <CardHeader>
          <CardTitle>
            {step === "email" && (isSignup ? "Create Your Account" : "Welcome Back")}
            {step === "sent" && "Check Your Email"}
            {step === "processing" && "Verifying..."}
            {step === "verified" && "You're In!"}
          </CardTitle>
          <CardDescription>
            {step === "email" && (isSignup ? "Get started with your free digital inventory" : "Sign in to access your inventory")}
            {step === "sent" && "We've sent you a magic link"}
            {step === "processing" && "Please wait while we verify your login"}
            {step === "verified" && "Redirecting to your dashboard..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === "email" && (
              <>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Button type="button" variant={mode === 'password' ? 'default' : 'outline'} onClick={() => setMode('password')} size="sm">
                    Use password
                  </Button>
                  <Button type="button" variant={mode === 'magic' ? 'default' : 'outline'} onClick={() => setMode('magic')} size="sm">
                    Magic link
                  </Button>
                </div>

                {mode === 'password' ? (
                  <form onSubmit={isSignup ? handlePasswordSignUp : handlePasswordSignIn} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className="text-lg"
                    />
                    <Input
                      type="password"
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="text-lg"
                    />
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-primary" 
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (isSignup ? "Creating..." : "Signing in...") : (isSignup ? "Create account" : "Sign in")}
                    </Button>
                    <p className="text-sm text-muted-foreground text-center">
                      {isSignup ? (
                        <>
                          Already have an account?{" "}
                          <button type="button" className="underline" onClick={() => setIsSignup(false)}>Sign in</button>
                        </>
                      ) : (
                        <>
                          New here?{" "}
                          <button type="button" className="underline" onClick={() => setIsSignup(true)}>Create an account</button>
                        </>
                      )}
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleSendMagicLink} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="text-lg"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-primary" 
                      size="lg"
                      disabled={loading}
                    >
                      <Mail className="mr-2 h-5 w-5" />
                      {loading ? "Sending..." : "Send Magic Link"}
                    </Button>
                    <p className="text-sm text-muted-foreground text-center">
                      No password needed. We'll email you a secure login link.
                    </p>
                  </form>
                )}
              </>
            )}

            {step === "sent" && (
              <div className="text-center space-y-4">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  We sent a login link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to continue.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => {
                    setStep("email");
                    setEmail("");
                  }}
                >
                  Use a different email
                </Button>
              </div>
            )}

            {step === "processing" && (
              <div className="text-center space-y-4">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <p className="text-muted-foreground">
                  {showRetry ? "This is taking longer than expected..." : "Verifying your login..."}
                </p>
                {showRetry && (
                  <div className="space-y-2">
                    <Button
                      onClick={() => {
                        // Use window.location to force full page reload
                        // This ensures AuthContext properly syncs the session
                        window.location.href = '/dashboard';
                      }}
                      className="w-full"
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setStep("email");
                        setShowRetry(false);
                        hasProcessedToken.current = false;
                      }}
                      className="w-full"
                    >
                      Try Again
                    </Button>
                  </div>
                )}
              </div>
            )}

            {step === "verified" && (
              <div className="text-center space-y-4">
                <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                  <Check className="h-8 w-8 text-white" />
                </div>
                <p className="font-semibold text-primary">Email Verified!</p>
                <p className="text-muted-foreground">
                  Taking you to your dashboard...
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => {
                    window.location.href = '/dashboard';
                  }}
                >
                  Not redirected? Go to Dashboard
                </Button>
              </div>
            )}

          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Free digital inventory · $9.99/month optional storage</p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
